import type {
  WorkspaceSeed,
  Project,
  SubProject,
  SpendRequest,
  SpendItem,
  SpendCategory,
  AuditEvent,
  Disbursement,
  EvidenceItem,
  Report,
  KycReview,
  Leak,
  AttachedDocument,
  AIFinding,
  SiteVisit,
  VisitJudgement,
  Declaration,
  FeedTransaction,
  Milestone,
  DemoProjectCreationRequest,
} from "./types";
import { MULTISIG_THRESHOLD, MAX_CUSTOM_QUESTIONS, COMPULSORY_SITE_VISIT_QUESTIONS } from "./types";
import { currentMilestoneId } from "./status";
import { isAboveStratusThreshold, SPEND_STRATUS_GATE_UGX } from "./spendThreshold";
import { CANNED_FINDINGS, cannedReviewForAsk } from "./heimdall";

/** The system identity Heimdall's actions are attributed to in the audit trail. */
const HEIMDALL_ACTOR = "per-heimdall";

// (AuthorityGrant mutations live below, near the spend lifecycle.)

// Pure updater functions over a workspace seed. Components call them via
// useStore().updateActiveSeed(seed => fn(seed, ...)). Each returns a new seed.

let counter = 0;
function uid(prefix: string): string {
  counter += 1;
  return `${prefix}-${Date.now().toString(36)}-${counter}`;
}
function now(): string {
  return new Date().toISOString();
}
/** A clean internal payment reference for a completed disbursement (not a chain hash). */
function paymentRef(): string {
  const d = new Date();
  const stamp = `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, "0")}${String(d.getUTCDate()).padStart(2, "0")}`;
  counter += 1;
  return `DIF-${stamp}-${String(counter).padStart(3, "0")}`;
}

function appendAudit(
  seed: WorkspaceSeed,
  event: Omit<AuditEvent, "id" | "timestamp">,
): AuditEvent {
  const e: AuditEvent = { ...event, id: uid("audit"), timestamp: now() };
  seed.auditEvents = [e, ...seed.auditEvents];
  return e;
}

function clone(seed: WorkspaceSeed): WorkspaceSeed {
  return structuredClone(seed);
}

// ---------------------------------------------------------------------------
// Project creation (ZEUS-125/132/133) — demo-mode simulation
// ---------------------------------------------------------------------------
//
// The real backend (server/routers/zeusProject.js) has ONE `project` table
// with a self-referencing parentprojectid — a sub-project IS a project row.
// The demo seed instead has two separate arrays/types, Project (top-level
// only) and SubProject (always has exactly one parent via projectId). This
// mirrors that split: a parentProjectId matching an existing demo Project
// creates a SubProject; its absence creates a top-level Project.

export function createZeusProject(
  seed: WorkspaceSeed,
  input: {
    name: string;
    description?: string;
    status?: Project["status"];
    parentProjectId?: string;
    /** ZEUS-138 — set when this create call is accepting a pending
     * project-creation request; marks the request accepted + links the
     * created project, mirroring the real backend's
     * linkAcceptedProjectCreationRequest. */
    requestId?: string;
    actorId: string;
    actorOrganisationId: string;
  },
): { seed: WorkspaceSeed; id: string } {
  const s = clone(seed);
  const id = uid("proj");
  const parent = input.parentProjectId ? s.projects.find((p) => p.id === input.parentProjectId) : undefined;

  // ZEUS-138 — accepting a request stamps the requester's own organisation as
  // lead contractor, so useVisibleProjects() (which scopes a contractor to
  // projects/sub-projects their org leads) actually shows it to them —
  // otherwise the empty default below leaves an accepted project invisible
  // to the very contractor who requested it.
  const acceptedRequest = input.requestId
    ? (s.projectCreationRequests ?? []).find((r) => r.id === input.requestId && r.status === "pending")
    : undefined;
  const leadContractorId = acceptedRequest?.requesterOrganisationId ?? "";

  if (parent) {
    const sub: SubProject = {
      id,
      projectId: parent.id,
      name: input.name,
      leadContractorId,
      budgeted: 0,
      spent: 0,
      evidenced: 0,
      status: input.status ?? "draft",
    };
    s.subProjects = [...s.subProjects, sub];
  } else {
    const project: Project = {
      id,
      programmeId: s.programme.id,
      name: input.name,
      region: "",
      district: "",
      budgeted: 0,
      spent: 0,
      evidenced: 0,
      redundancy: 0,
      leadContractorId,
      status: input.status ?? "draft",
      description: input.description,
    };
    s.projects = [...s.projects, project];
  }

  if (acceptedRequest) {
    acceptedRequest.status = "accepted";
    acceptedRequest.createdProjectId = id;
  }

  appendAudit(s, {
    actorId: input.actorId,
    actorOrganisationId: input.actorOrganisationId,
    class: "system",
    description: parent
      ? `Created sub-project "${input.name}" under "${parent.name}".`
      : `Created project "${input.name}".`,
    scope: { programmeId: s.programme.id, projectId: parent ? parent.id : id, subProjectId: parent ? id : undefined },
  });

  return { seed: s, id };
}

/**
 * ZEUS-129/138 — contractor/subcontractor submits a project-creation
 * request; demo-mode mirror of the real submitProjectCreationRequest
 * helper. Persists a re-fetchable pending row (unlike the earlier
 * ZEUS-132/133 simplification, which only faked an audit entry) so the
 * new stratus_admin review tab has something real to show in demo mode.
 */
export function submitProjectCreationRequest(
  seed: WorkspaceSeed,
  input: {
    proposedName: string;
    proposedDescription: string;
    justification: string;
    requesterRole?: string;
    parentProjectId?: string;
    actorId: string;
    actorOrganisationId: string;
  },
): { seed: WorkspaceSeed; id: string } {
  const s = clone(seed);
  const id = uid("req");

  const request: DemoProjectCreationRequest = {
    id,
    requesterActorId: input.actorId,
    requesterOrganisationId: input.actorOrganisationId,
    requesterRole: input.requesterRole || "prime_lead",
    proposedName: input.proposedName,
    proposedDescription: input.proposedDescription,
    justification: input.justification,
    parentProjectId: input.parentProjectId,
    status: "pending",
    createdAt: now(),
  };
  s.projectCreationRequests = [request, ...(s.projectCreationRequests ?? [])];

  appendAudit(s, {
    actorId: input.actorId,
    actorOrganisationId: input.actorOrganisationId,
    class: "system",
    description: `Requested a new project: "${input.proposedName}".`,
    scope: { programmeId: s.programme.id },
  });

  return { seed: s, id };
}

/**
 * stratus_admin rejects a pending demo-mode project-creation request —
 * mirrors the real rejectProjectCreationRequest helper. No-op (returns the
 * seed unchanged) for an unknown id or a request that isn't pending, same
 * defensive behaviour as editZeusProject above.
 */
export function rejectProjectCreationRequest(
  seed: WorkspaceSeed,
  requestId: string,
  reason: string,
  actor: { actorId: string; actorOrganisationId: string },
): WorkspaceSeed {
  const s = clone(seed);
  const request = (s.projectCreationRequests ?? []).find((r) => r.id === requestId);
  if (!request || request.status !== "pending") return s;

  request.status = "rejected";
  request.rejectionReason = reason;

  appendAudit(s, {
    actorId: actor.actorId,
    actorOrganisationId: actor.actorOrganisationId,
    class: "system",
    description: `Rejected project request "${request.proposedName}": ${reason}`,
    scope: { programmeId: s.programme.id },
  });

  return s;
}

export function editZeusProject(
  seed: WorkspaceSeed,
  projectId: string,
  patch: { name?: string; description?: string; region?: string; district?: string; budgeted?: number },
  actor: { actorId: string; actorOrganisationId: string },
): WorkspaceSeed {
  const s = clone(seed);
  const project = s.projects.find((p) => p.id === projectId);
  const sub = project ? undefined : s.subProjects.find((sp) => sp.id === projectId);
  const target = project ?? sub;
  if (!target) return s;

  Object.assign(target, patch);

  appendAudit(s, {
    actorId: actor.actorId,
    actorOrganisationId: actor.actorOrganisationId,
    class: "status_change",
    description: `Edited "${target.name}".`,
    scope: {
      programmeId: s.programme.id,
      projectId: project ? project.id : (sub as SubProject).projectId,
      subProjectId: sub ? sub.id : undefined,
    },
  });

  return s;
}

/**
 * ZEUS-142 — demo-mode mirror of the real PATCH /:id/status toggle. Free
 * choice among the 3 ZEUS governance statuses, no adjacency restriction
 * (matches the backend, which allows any-to-any).
 */
export function changeZeusProjectStatus(
  seed: WorkspaceSeed,
  projectId: string,
  status: Project["status"],
  actor: { actorId: string; actorOrganisationId: string },
): WorkspaceSeed {
  const s = clone(seed);
  const project = s.projects.find((p) => p.id === projectId);
  const sub = project ? undefined : s.subProjects.find((sp) => sp.id === projectId);
  const target = project ?? sub;
  if (!target) return s;

  const previousStatus = target.status;
  target.status = status;

  appendAudit(s, {
    actorId: actor.actorId,
    actorOrganisationId: actor.actorOrganisationId,
    class: "status_change",
    description: `Changed "${target.name}" status from ${previousStatus} to ${status}.`,
    scope: {
      programmeId: s.programme.id,
      projectId: project ? project.id : (sub as SubProject).projectId,
      subProjectId: sub ? sub.id : undefined,
    },
  });

  return s;
}

// ---------------------------------------------------------------------------
// Spend request lifecycle
// ---------------------------------------------------------------------------

export function approveSpend(seed: WorkspaceSeed, id: string, reviewerId: string): WorkspaceSeed {
  const s = clone(seed);
  const r = s.spendRequests.find((x) => x.id === id);
  if (!r) return s;
  // Above-threshold asks are routed to Stratus first (spec §6.3): Stratus must mark the ask
  // independently verified before the contractor lead can approve it. The UI already hides
  // the action until then; this guard stops any code path from skipping the gate.
  if (isAboveStratusThreshold(r) && !r.verifiedAt) return s;
  r.state = "approved_for_spend";
  r.reviewerId = reviewerId;
  r.reviewedAt = now();
  appendAudit(s, {
    actorId: reviewerId,
    actorOrganisationId: r.organisationId,
    class: "approval",
    description: `Approved spend ask '${r.title}' · ${human(r.amount)} on ${projectName(s, r.projectId)}.`,
    scope: { programmeId: s.programme.id, projectId: r.projectId, subProjectId: r.subProjectId },
  });
  return s;
}

export function rejectSpend(seed: WorkspaceSeed, id: string, reviewerId: string, reason: string): WorkspaceSeed {
  const s = clone(seed);
  const r = s.spendRequests.find((x) => x.id === id);
  if (!r) return s;
  r.state = "rejected";
  r.reviewerId = reviewerId;
  r.reviewedAt = now();
  r.rejectionReason = reason;
  appendAudit(s, {
    actorId: reviewerId,
    actorOrganisationId: r.organisationId,
    class: "approval",
    description: `Rejected spend ask '${r.title}': ${reason}`,
    scope: { programmeId: s.programme.id, projectId: r.projectId, subProjectId: r.subProjectId },
  });
  return s;
}

export function querySpend(seed: WorkspaceSeed, id: string, reviewerId: string, comment: string): WorkspaceSeed {
  const s = clone(seed);
  const r = s.spendRequests.find((x) => x.id === id);
  if (!r) return s;
  r.state = "proposed";
  r.reviewerComment = comment;
  r.thread = [...(r.thread ?? []), { id: uid("c"), authorId: reviewerId, body: comment, at: now(), kind: "query" }];
  appendAudit(s, {
    actorId: reviewerId,
    actorOrganisationId: r.organisationId,
    class: "approval",
    description: `Queried spend ask '${r.title}': ${comment}`,
    scope: { programmeId: s.programme.id, projectId: r.projectId, subProjectId: r.subProjectId },
  });
  return s;
}

export function raiseSpend(
  seed: WorkspaceSeed,
  input: {
    reporterId: string;
    organisationId: string;
    projectId: string;
    subProjectId?: string;
    milestoneId?: string;
    title: string;
    amount: number;
    category?: SpendCategory;
    supplier?: string;
    purpose: string;
    path?: "pre_auth" | "recorded";
    documents?: AttachedDocument[];
    items?: { description: string; amount: number; category?: SpendCategory; supplier?: string }[];
  },
): { seed: WorkspaceSeed; id: string } {
  const s = clone(seed);
  const id = uid("sr");
  const overThreshold = input.amount >= SPEND_STRATUS_GATE_UGX;
  // Recorded spend is self-authorised (within the reporter's delegated authority): it skips
  // the approval queue and lands awaiting evidence. Pre-auth spend is proposed for approval.
  const recorded = input.path === "recorded" && !overThreshold;
  const req: SpendRequest = {
    id,
    reporterId: input.reporterId,
    organisationId: input.organisationId,
    projectId: input.projectId,
    subProjectId: input.subProjectId,
    milestoneId: input.milestoneId,
    title: input.title,
    amount: input.amount,
    currency: "UGX",
    category: input.category,
    supplier: input.supplier,
    purpose: input.purpose,
    documents: input.documents ?? [],
    state: recorded ? "approved_for_spend" : "proposed",
    submittedAt: now(),
    path: recorded ? "recorded" : "pre_auth",
    complianceState: overThreshold ? "pending_declaration" : "not_required",
    thresholdFlag: overThreshold
      ? { rule: "Single spend above 400M UGX requires a compliance declaration", routedTo: "stratus" }
      : undefined,
  };
  s.spendRequests = [req, ...s.spendRequests];
  if (input.items?.length) {
    for (const it of input.items) {
      s.spendItems = [
        ...s.spendItems,
        {
          id: uid("si"),
          spendRequestId: id,
          description: it.description,
          amount: it.amount,
          currency: "UGX",
          category: it.category ?? input.category,
          supplier: it.supplier ?? input.supplier,
          evidence: [],
          committedAt: now(),
        },
      ];
    }
  }
  appendAudit(s, {
    actorId: input.reporterId,
    actorOrganisationId: input.organisationId,
    class: "spend",
    description: recorded
      ? `Recorded spend within authority '${input.title}' · ${human(input.amount)} on ${projectName(s, input.projectId)}.`
      : `Raised spend ask for authorisation '${input.title}' · ${human(input.amount)} on ${projectName(s, input.projectId)}.`,
    scope: { programmeId: s.programme.id, projectId: input.projectId, subProjectId: input.subProjectId },
  });
  return { seed: s, id };
}

export function addSpendItem(
  seed: WorkspaceSeed,
  spendRequestId: string,
  item: Omit<SpendItem, "id" | "spendRequestId" | "committedAt" | "currency">,
): WorkspaceSeed {
  const s = clone(seed);
  const r = s.spendRequests.find((x) => x.id === spendRequestId);
  if (!r) return s;
  const newItem: SpendItem = {
    ...item,
    id: uid("si"),
    spendRequestId,
    currency: "UGX",
    committedAt: now(),
  };
  s.spendItems = [...s.spendItems, newItem];
  if (r.state === "approved_for_spend") r.state = "committed";
  return s;
}

export function submitEvidence(
  seed: WorkspaceSeed,
  spendRequestId: string,
  actorId: string,
  evidence?: {
    type?: EvidenceItem["type"];
    note?: string;
    geo?: { lat: number; lng: number };
    linkedItemId?: string;
    url?: string;
    filename?: string;
  },
): WorkspaceSeed {
  const s = clone(seed);
  const r = s.spendRequests.find((x) => x.id === spendRequestId);
  if (!r) return s;
  const isResubmission = (r.evidenceIds ?? []).length > 0;
  // Advance to evidence_submitted only from the post-approval, pre-confirmation
  // window. Re-submissions stay in evidence_submitted; a confirmed/rejected ask is
  // never knocked back (the reporter can't reach the screen for those anyway).
  if (r.state === "approved_for_spend" || r.state === "committed" || r.state === "evidence_submitted") {
    r.state = "evidence_submitted";
  }
  const ev: EvidenceItem = {
    id: uid("ev"),
    type: evidence?.type ?? "photo",
    url: evidence?.url ?? "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1200&q=60",
    filename: evidence?.filename ?? `evidence-${r.id}.jpg`,
    note: evidence?.note,
    geo: evidence?.geo,
    capturedAt: now(),
    submittedById: actorId,
    linkedAskId: r.id,
    linkedItemId: evidence?.linkedItemId,
  };
  s.evidence = [ev, ...(s.evidence ?? [])];
  r.evidenceIds = [...(r.evidenceIds ?? []), ev.id];
  appendAudit(s, {
    actorId,
    actorOrganisationId: r.organisationId,
    class: "evidence",
    description: isResubmission
      ? `Added further evidence for '${r.title}' · ${human(r.amount)}.`
      : `Submitted evidence pack for '${r.title}' · ${human(r.amount)}.`,
    scope: { programmeId: s.programme.id, projectId: r.projectId, subProjectId: r.subProjectId },
  });
  return s;
}

export function confirmSpend(seed: WorkspaceSeed, spendRequestId: string, actorId: string): WorkspaceSeed {
  const s = clone(seed);
  const r = s.spendRequests.find((x) => x.id === spendRequestId);
  if (!r) return s;
  r.state = "confirmed";
  // Flow contributing amounts into the project's spent + evidenced figures.
  const project = s.projects.find((p) => p.id === r.projectId);
  if (project) project.evidenced += r.amount;
  appendAudit(s, {
    actorId,
    actorOrganisationId: r.organisationId,
    class: "evidence",
    description: `Confirmed spend '${r.title}' · ${human(r.amount)}.`,
    scope: { programmeId: s.programme.id, projectId: r.projectId, subProjectId: r.subProjectId },
  });
  return s;
}

// ---------------------------------------------------------------------------
// Disbursements (Stratus → Funder)
// ---------------------------------------------------------------------------

export function routeDisbursement(seed: WorkspaceSeed, id: string, actorId: string, note?: string): WorkspaceSeed {
  const s = clone(seed);
  const d = s.disbursements.find((x) => x.id === id);
  if (!d) return s;
  d.state = "ready_for_funder";
  d.routedById = actorId;
  d.routedAt = now();
  if (note) d.stratusRecommendation = note;
  // Attach the confirmed spend on the milestone as the disbursement basis (spec §2.1).
  const ms = s.milestones.find((m) => m.id === d.milestoneId);
  if (ms) {
    const confirmed = s.spendRequests.filter(
      (r) => r.milestoneId === ms.id && r.state === "confirmed",
    );
    if (confirmed.length) d.basisAskIds = confirmed.map((r) => r.id);
  }
  appendAudit(s, {
    actorId,
    actorOrganisationId: s.programme.oversightId,
    class: "approval",
    description: `Routed milestone disbursement to the Danish Impact Fund · ${human(d.amount)}.`,
    scope: { programmeId: s.programme.id },
  });
  return s;
}

export function approveDisbursement(seed: WorkspaceSeed, id: string, approverId: string): WorkspaceSeed {
  const s = clone(seed);
  const d = s.disbursements.find((x) => x.id === id);
  if (!d) return s;
  const ms = s.milestones.find((m) => m.id === d.milestoneId);

  // Record the sign-off (segregation: one approver counts once).
  d.approvals = [...(d.approvals ?? []).filter((a) => a.approverId !== approverId), { approverId, at: now() }];
  const needsTwo = d.amount >= MULTISIG_THRESHOLD;

  if (needsTwo && d.approvals.length < 2) {
    d.state = "approved"; // one signature; a second is required before release
    appendAudit(s, {
      actorId: approverId,
      actorOrganisationId: s.programme.funderId,
      class: "approval",
      description: `Signed off milestone disbursement '${ms?.name ?? d.milestoneId}' (1 of 2) · ${human(d.amount)}. A second authoriser is required before release.`,
      scope: { programmeId: s.programme.id, projectId: ms?.projectId },
    });
    return s;
  }

  d.state = "completed";
  d.funderApproverId = approverId;
  d.approvedAt = now();
  d.bankingTransactionRef = paymentRef();
  if (ms) {
    ms.status = "disbursed";
    // Re-derive the current milestone per track (spec §3.3 — one current per track).
    // Disbursing out of order can no longer strand two milestones as "current"; the
    // current is simply the earliest still-open gate in the track.
    recomputeCurrentMilestones(s);
  }
  // Sovereignty Bill: generate an inbound-funds declaration for oversight to review and file.
  const declProject = ms ? s.projects.find((p) => p.id === ms.projectId) : undefined;
  const declRecipient = declProject ? s.organisations.find((o) => o.id === declProject.leadContractorId) : undefined;
  s.declarations = [
    {
      id: uid("decl"),
      programmeId: s.programme.id,
      disbursementId: d.id,
      milestoneId: d.milestoneId,
      title: `Inbound funds · ${ms?.name ?? "milestone"}`,
      receivingEntity: declRecipient?.name ?? s.programme.name,
      purpose: `Milestone disbursement for ${declProject?.name ?? s.programme.name}`,
      amount: d.amount,
      date: now(),
      authority: "Ministry of Finance",
      status: "draft",
      generatedAt: now(),
    },
    ...(s.declarations ?? []),
  ];
  appendAudit(s, {
    actorId: approverId,
    actorOrganisationId: s.programme.funderId,
    class: "approval",
    description: `Approved milestone disbursement '${ms?.name ?? d.milestoneId}' · ${human(d.amount)}${needsTwo ? " (second of two sign-offs)" : ""}. Next milestone unlocked for spend.`,
    scope: { programmeId: s.programme.id, projectId: ms?.projectId },
  });
  return s;
}

/** The next milestone for the same project, by `order` if present else seed order. */
/**
 * Re-derive the single "current" milestone flag for every track (spec §3.3: one current
 * milestone per project *or* sub-project sequence). Current = the earliest milestone not
 * yet disbursed or closed. Run after any change to milestone status so out-of-order
 * disbursement can never leave two milestones flagged current.
 */
function recomputeCurrentMilestones(seed: WorkspaceSeed): void {
  const byTrack = new Map<string, Milestone[]>();
  for (const m of seed.milestones) {
    const key = `${m.projectId}::${m.subProjectId ?? ""}`;
    const track = byTrack.get(key) ?? [];
    track.push(m);
    byTrack.set(key, track);
  }
  for (const track of byTrack.values()) {
    const currentId = currentMilestoneId(track);
    for (const m of track) m.current = m.id === currentId;
  }
}

export function queryDisbursement(seed: WorkspaceSeed, id: string, approverId: string, note: string): WorkspaceSeed {
  const s = clone(seed);
  const d = s.disbursements.find((x) => x.id === id);
  if (!d) return s;
  d.state = "queried";
  d.funderNote = note;
  appendAudit(s, {
    actorId: approverId,
    actorOrganisationId: s.programme.funderId,
    class: "approval",
    description: `Queried milestone disbursement: ${note}`,
    scope: { programmeId: s.programme.id },
  });
  return s;
}

export function holdDisbursement(seed: WorkspaceSeed, id: string, approverId: string, note?: string): WorkspaceSeed {
  const s = clone(seed);
  const d = s.disbursements.find((x) => x.id === id);
  if (!d) return s;
  d.state = "held";
  if (note) d.funderNote = note;
  appendAudit(s, {
    actorId: approverId,
    actorOrganisationId: s.programme.funderId,
    class: "status_change",
    description: `Paused milestone disbursement · ${human(d.amount)}${note ? ` · ${note}` : ""}.`,
    scope: { programmeId: s.programme.id },
  });
  return s;
}

export function setDisbursementState(seed: WorkspaceSeed, id: string, state: Disbursement["state"]): WorkspaceSeed {
  const s = clone(seed);
  const d = s.disbursements.find((x) => x.id === id);
  if (d) d.state = state;
  return s;
}

// ---------------------------------------------------------------------------
// KYC reviews
// ---------------------------------------------------------------------------

export function kycSetAccepted(seed: WorkspaceSeed, reviewId: string, docKey: string, accepted: boolean): WorkspaceSeed {
  const s = clone(seed);
  const k = s.kycReviews.find((x) => x.id === reviewId);
  if (!k) return s;
  const doc = k.requiredDocuments.find((d) => d.key === docKey);
  if (doc) doc.accepted = accepted;
  return s;
}

export function kycDecision(
  seed: WorkspaceSeed,
  reviewId: string,
  reviewerId: string,
  decision: KycReview["state"],
  comment?: string,
): WorkspaceSeed {
  const s = clone(seed);
  const k = s.kycReviews.find((x) => x.id === reviewId);
  if (!k) return s;
  k.state = decision;
  k.reviewerId = reviewerId;
  k.reviewedAt = now();
  if (comment) {
    k.reviewerComments = [
      ...k.reviewerComments,
      { id: uid("c"), authorId: reviewerId, body: comment, createdAt: now(), visibility: "shared_with_contractor" },
    ];
  }
  const org = s.organisations.find((o) => o.id === k.organisationId);
  if (org) {
    org.kycState = decision === "approved" ? "approved" : decision === "rejected" ? "rejected" : decision === "queried" ? "queried" : "pending_review";
  }
  const label = decision === "approved" ? "Approved" : decision === "queried" ? "Queried" : decision === "rejected" ? "Rejected" : "Updated";
  appendAudit(s, {
    actorId: reviewerId,
    actorOrganisationId: s.programme.oversightId,
    class: "kyc",
    description: `${label} KYC submission for ${org?.name ?? k.organisationId}${comment ? ` · ${comment}` : ""}.`,
    scope: { programmeId: s.programme.id },
  });
  return s;
}

// ---------------------------------------------------------------------------
// Leaks
// ---------------------------------------------------------------------------

export function setLeakState(seed: WorkspaceSeed, id: string, state: Leak["state"], actorId: string, note?: string): WorkspaceSeed {
  const s = clone(seed);
  const l = s.leaks.find((x) => x.id === id);
  if (!l) return s;
  l.state = state;
  if (state === "investigating") l.assignedToId = actorId;
  appendAudit(s, {
    actorId,
    actorOrganisationId: s.programme.oversightId,
    class: "status_change",
    description: `Marked leak '${truncate(l.title)}' as ${state.replace("_", " ")}${note ? ` · ${note}` : ""}.`,
    scope: { programmeId: s.programme.id, projectId: l.projectId },
  });
  return s;
}

// ---------------------------------------------------------------------------
// Notifications
// ---------------------------------------------------------------------------

export function markNotificationRead(seed: WorkspaceSeed, id: string): WorkspaceSeed {
  const s = clone(seed);
  const n = s.notifications.find((x) => x.id === id);
  if (n) n.read = true;
  return s;
}

export function markAllNotificationsRead(seed: WorkspaceSeed): WorkspaceSeed {
  const s = clone(seed);
  s.notifications = s.notifications.map((n) => ({ ...n, read: true }));
  return s;
}

// ---------------------------------------------------------------------------
// Cascading delegated authority (v3 §Chunk 1)
// ---------------------------------------------------------------------------

export function setGrantThreshold(seed: WorkspaceSeed, grantId: string, threshold: number, actorId: string): WorkspaceSeed {
  const s = clone(seed);
  const g = (s.authorityGrants ?? []).find((x) => x.id === grantId);
  if (!g) return s;
  const prev = g.selfAuthThreshold;
  g.selfAuthThreshold = Math.max(0, threshold);
  const grantee = s.people.find((p) => p.id === g.granteeId);
  appendAudit(s, {
    actorId,
    actorOrganisationId: g.granteeOrgId,
    class: "status_change",
    description: `Set ${grantee?.fullName ?? g.granteeId}'s spending threshold on ${g.scope.label ?? "this scope"} from ${human(prev)} to ${human(g.selfAuthThreshold)}.`,
    scope: { programmeId: s.programme.id, projectId: g.scope.projectId, subProjectId: g.scope.subProjectId },
  });
  return s;
}

// ---------------------------------------------------------------------------
// Connections + reconciliation (v3 §Chunk 3)
// ---------------------------------------------------------------------------

const CONNECTION_KIND_LABEL: Record<string, string> = {
  mobile_money: "mobile money", bank: "bank feed", accounting: "accounting",
  invoice_inbox: "invoice inbox", kyc: "KYC provider", gov_records: "government records",
};

export function setConnectionStatus(seed: WorkspaceSeed, connectionId: string, status: "connected" | "not_connected", actorId: string): WorkspaceSeed {
  const s = clone(seed);
  const c = (s.connections ?? []).find((x) => x.id === connectionId);
  if (!c) return s;
  c.status = status;
  if (status === "connected") {
    c.connectedAt = c.connectedAt ?? now();
    c.lastSyncAt = now();
  }
  appendAudit(s, {
    actorId,
    actorOrganisationId: c.organisationId,
    class: "system",
    description: `${status === "connected" ? "Connected" : "Disconnected"} ${c.provider} (${CONNECTION_KIND_LABEL[c.kind] ?? c.kind}).`,
    scope: { programmeId: s.programme.id },
  });
  return s;
}

export function syncConnection(seed: WorkspaceSeed, connectionId: string): WorkspaceSeed {
  const s = clone(seed);
  const c = (s.connections ?? []).find((x) => x.id === connectionId);
  if (c) c.lastSyncAt = now();
  return s;
}

/** Reconcile a feed transaction: match it to an existing ask, or import it as confirmed spend. */
export function reconcileFeedTxn(
  seed: WorkspaceSeed,
  txnId: string,
  actorId: string,
  opts: { action: "match"; askId: string } | { action: "import" },
): WorkspaceSeed {
  const s = clone(seed);
  const t = (s.feedTransactions ?? []).find((x) => x.id === txnId);
  if (!t) return s;
  const ref = `STMT-${t.id}`;

  if (opts.action === "match") {
    const r = s.spendRequests.find((x) => x.id === opts.askId);
    if (r) r.statementMatchRef = ref;
    t.status = "reconciled";
    t.reconciledToAskId = opts.askId;
    appendAudit(s, {
      actorId,
      actorOrganisationId: t.organisationId,
      class: "spend",
      description: `Matched feed transaction '${truncate(t.description)}' to '${r?.title ?? opts.askId}' · ${human(t.amount)} (statement-matched).`,
      scope: { programmeId: s.programme.id, projectId: r?.projectId, subProjectId: r?.subProjectId },
    });
    return s;
  }

  // Import as a confirmed, statement-matched spend.
  const id = uid("sr");
  const req: SpendRequest = {
    id,
    reporterId: actorId,
    organisationId: t.organisationId,
    projectId: t.suggestedProjectId ?? s.projects[0]?.id ?? "",
    subProjectId: t.suggestedSubProjectId,
    title: t.description,
    amount: t.amount,
    currency: "UGX",
    category: t.suggestedCategory,
    supplier: t.counterparty,
    purpose: "Reconciled from a connected money feed.",
    documents: [],
    state: "confirmed",
    submittedAt: now(),
    path: "recorded",
    statementMatchRef: ref,
  };
  s.spendRequests = [req, ...s.spendRequests];
  t.status = "reconciled";
  t.reconciledToAskId = id;
  appendAudit(s, {
    actorId,
    actorOrganisationId: t.organisationId,
    class: "spend",
    description: `Reconciled feed transaction '${truncate(t.description)}' as confirmed spend · ${human(t.amount)} (statement-matched).`,
    scope: { programmeId: s.programme.id, projectId: req.projectId, subProjectId: req.subProjectId },
  });
  return s;
}

// ---------------------------------------------------------------------------
// Reports (spec §5.1)
// ---------------------------------------------------------------------------

export function createReport(
  seed: WorkspaceSeed,
  input: {
    title: string;
    type: Report["type"];
    scope: Report["scope"];
    projectId?: string;
    audience: string;
    periodLabel: string;
    periodStart: string;
    periodEnd: string;
    authorId: string;
    notes?: string;
    visibleTo: Report["visibleTo"];
    /** When true, the report is marked as a Heimdall draft awaiting human review. */
    draftedByAI?: boolean;
  },
  /** In production mode, supply the server-assigned UUID so local and remote IDs stay in sync. */
  overrideId?: string,
): { seed: WorkspaceSeed; id: string } {
  const s = clone(seed);
  const id = overrideId ?? uid("rep");
  // Compose a narrative body from the live seed (spend by category, milestones, evidence).
  const scopedProjects = input.projectId
    ? s.projects.filter((p) => p.id === input.projectId)
    : s.projects;
  const totalBudget = scopedProjects.reduce((a, p) => a + p.budgeted, 0);
  const totalSpent = scopedProjects.reduce((a, p) => a + p.spent, 0);
  const totalEvidenced = scopedProjects.reduce((a, p) => a + p.evidenced, 0);
  const confirmedCount = s.spendRequests.filter((r) => r.state === "confirmed").length;
  const disbursed = s.milestones.filter((m) => m.status === "disbursed").length;
  const report: Report = {
    id,
    title: input.title,
    type: input.type,
    scope: input.scope,
    programmeId: s.programme.id,
    projectId: input.projectId,
    audience: input.audience,
    periodLabel: input.periodLabel,
    periodStart: input.periodStart,
    periodEnd: input.periodEnd,
    createdAt: now(),
    updatedAt: now(),
    authorId: input.authorId,
    limitedData: s.spendRequests.length < 3,
    notes: input.notes,
    visibleTo: input.visibleTo,
    kind: input.draftedByAI ? "progress" : undefined,
    draftedByAI: input.draftedByAI || undefined,
    sections: [
      {
        heading: "Introduction",
        body: `This ${input.type.toLowerCase()} report covers ${input.periodLabel} for ${input.projectId ? scopedProjects[0]?.name ?? "the project" : s.programme.name}. It summarises delivery progress, confirmed field spend and the evidence recorded during the period.`,
      },
      {
        heading: "Infrastructure and resources",
        body: `${scopedProjects.length} site${scopedProjects.length === 1 ? "" : "s"} are in scope, with a combined budget of ${human(totalBudget)}. Observed spend stands at ${human(totalSpent)}, of which ${human(totalEvidenced)} is evidenced and confirmed.`,
      },
      {
        heading: "Milestones",
        body: `${disbursed} milestone${disbursed === 1 ? " has" : "s have"} been disbursed against confirmed spend. The current binding milestone is the structural frame across the active stations.`,
      },
      {
        heading: "Financials by category",
        body: `Spend is tracked across the construction taxonomy (civil works, materials, labour, equipment and plant, logistics, professional fees, health and safety, contingency). The financial appendix breaks down committed and confirmed spend per category for the period.`,
      },
      {
        heading: "Evidence highlights",
        body: `${confirmedCount} spend ask${confirmedCount === 1 ? "" : "s"} were evidenced and confirmed in the period, each with receipts, site photographs and a geo-tag attached to the line item.`,
      },
      {
        heading: "Outlook",
        body: input.notes?.trim()
          ? input.notes.trim()
          : `Delivery remains broadly on pace. Attention is on the sites carrying schedule drift; the next disbursement gate depends on completing the current milestone's evidence pack.`,
      },
    ],
  };
  s.reports = [report, ...(s.reports ?? [])];
  return { seed: s, id };
}

/** Approve and file a Heimdall-drafted report, recording the reviewing human. */
export function approveReportDraft(seed: WorkspaceSeed, id: string, reviewerId: string): WorkspaceSeed {
  const s = clone(seed);
  const r = s.reports?.find((x) => x.id === id);
  if (!r) return s;
  r.reviewedById = reviewerId;
  r.updatedAt = now();
  appendAudit(s, {
    actorId: reviewerId,
    actorOrganisationId: s.programme.oversightId,
    class: "status_change",
    description: `Reviewed and filed the Heimdall-drafted report '${r.title}'.`,
    scope: { programmeId: s.programme.id, projectId: r.projectId },
  });
  return s;
}

export function editReport(seed: WorkspaceSeed, id: string, patch: Partial<Pick<Report, "title" | "notes" | "sections">>): WorkspaceSeed {
  const s = clone(seed);
  const r = s.reports?.find((x) => x.id === id);
  if (!r) return s;
  if (patch.title !== undefined) r.title = patch.title;
  if (patch.notes !== undefined) r.notes = patch.notes;
  if (patch.sections !== undefined) r.sections = patch.sections;
  r.updatedAt = now();
  return s;
}

// ---------------------------------------------------------------------------
// Spend ingestion (mocked) — document parse + CSV reconcile (spec §2.8)
// ---------------------------------------------------------------------------

export function createSpendFromDocument(
  seed: WorkspaceSeed,
  input: { reporterId: string; organisationId: string; projectId: string; subProjectId?: string; title: string; amount: number; category?: SpendCategory; supplier?: string },
): { seed: WorkspaceSeed; id: string } {
  return raiseSpend(seed, {
    ...input,
    purpose: `Imported from supplier document. ${input.supplier ? `Supplier: ${input.supplier}.` : ""}`.trim(),
  });
}

export function importSpendFromCsv(
  seed: WorkspaceSeed,
  rows: { description: string; amount: number; category?: SpendCategory; supplier?: string }[],
  ctx: { reporterId: string; organisationId: string; projectId: string; subProjectId?: string },
): WorkspaceSeed {
  let s = clone(seed);
  for (const row of rows) {
    const res = raiseSpend(s, {
      reporterId: ctx.reporterId,
      organisationId: ctx.organisationId,
      projectId: ctx.projectId,
      subProjectId: ctx.subProjectId,
      title: row.description,
      amount: row.amount,
      category: row.category,
      supplier: row.supplier,
      purpose: "Reconciled from an imported mobile-money / bank statement.",
    });
    s = res.seed;
    // Imported rows arrive as already-confirmed spend, matched to the statement they came from.
    const r = s.spendRequests.find((x) => x.id === res.id);
    if (r) {
      r.state = "confirmed";
      r.statementMatchRef = uid("stmt");
    }
  }
  return s;
}

// ---------------------------------------------------------------------------
// Assurance (v3 §Chunk 2)
// ---------------------------------------------------------------------------

export function verifySpend(seed: WorkspaceSeed, askId: string, actorId: string, note?: string): WorkspaceSeed {
  const s = clone(seed);
  const r = s.spendRequests.find((x) => x.id === askId);
  if (!r) return s;
  r.verifiedById = actorId;
  r.verifiedAt = now();
  if (note) r.verificationNote = note;
  appendAudit(s, {
    actorId,
    actorOrganisationId: s.programme.oversightId,
    class: "evidence",
    description: `Independently verified spend '${r.title}' · ${human(r.amount)}${note ? ` · ${note}` : ""}.`,
    scope: { programmeId: s.programme.id, projectId: r.projectId, subProjectId: r.subProjectId },
  });
  return s;
}

export function addSpendComment(seed: WorkspaceSeed, askId: string, actorId: string, body: string): WorkspaceSeed {
  const s = clone(seed);
  const r = s.spendRequests.find((x) => x.id === askId);
  if (!r) return s;
  r.thread = [...(r.thread ?? []), { id: uid("c"), authorId: actorId, body, at: now(), kind: "comment" }];
  appendAudit(s, {
    actorId,
    actorOrganisationId: r.organisationId,
    class: "status_change",
    description: `Commented on '${r.title}': ${truncate(body)}`,
    scope: { programmeId: s.programme.id, projectId: r.projectId, subProjectId: r.subProjectId },
  });
  return s;
}

export function explainGap(seed: WorkspaceSeed, askId: string, actorId: string, body: string): WorkspaceSeed {
  const s = clone(seed);
  const r = s.spendRequests.find((x) => x.id === askId);
  if (!r) return s;
  r.gapExplanation = { body, authorId: actorId, createdAt: now() };
  appendAudit(s, {
    actorId,
    actorOrganisationId: r.organisationId,
    class: "evidence",
    description: `Explained an assurance gap on '${r.title}': ${truncate(body)}`,
    scope: { programmeId: s.programme.id, projectId: r.projectId, subProjectId: r.subProjectId },
  });
  return s;
}

// ---------------------------------------------------------------------------
// Programme lifecycle + staged funder visibility (pilot-readiness addendum §2)
// ---------------------------------------------------------------------------

const PHASE_LABEL: Record<string, string> = {
  feasibility: "feasibility", pilot: "pilot", full: "full programme", complete: "complete",
};

export function setProgrammePhase(seed: WorkspaceSeed, phase: NonNullable<WorkspaceSeed["programme"]["phase"]>, actorId: string): WorkspaceSeed {
  const s = clone(seed);
  const prev = s.programme.phase;
  s.programme.phase = phase;
  appendAudit(s, {
    actorId,
    actorOrganisationId: s.programme.oversightId,
    class: "status_change",
    description: `Moved ${s.programme.name} from ${PHASE_LABEL[prev ?? "feasibility"]} to ${PHASE_LABEL[phase]}.`,
    scope: { programmeId: s.programme.id },
  });
  return s;
}

/** Grant the funder access to a programme that was being prepared (staged visibility). */
export function shareWithFunder(seed: WorkspaceSeed, actorId: string): WorkspaceSeed {
  const s = clone(seed);
  if (s.programme.funderVisibility === "shared") return s;
  s.programme.funderVisibility = "shared";
  appendAudit(s, {
    actorId,
    actorOrganisationId: s.programme.oversightId,
    class: "status_change",
    description: `Shared ${s.programme.name} with the funder. The Danish Impact Fund now has access.`,
    scope: { programmeId: s.programme.id },
  });
  return s;
}

// ---------------------------------------------------------------------------
// Heimdall — the AI analyst layer (Heimdall brief)
// Advisory only: these never change a spend's lifecycle state; a human still gates it.
// ---------------------------------------------------------------------------

/**
 * Run Heimdall's automatic review over an ask and apply its canned verdict (Heimdall brief §4).
 * Adds any findings to the store and stamps the ask's review state. The staged 2–4s "reviewing"
 * delay is a UI concern; this mutation applies the result the indicator resolves to.
 */
export function runAIReview(seed: WorkspaceSeed, askId: string): WorkspaceSeed {
  const s = clone(seed);
  const r = s.spendRequests.find((x) => x.id === askId);
  if (!r) return s;
  const verdict = cannedReviewForAsk(r);
  const findingIds: string[] = [];
  if (verdict.state === "flagged" && verdict.cannedKey) {
    const template = CANNED_FINDINGS[verdict.cannedKey];
    const finding: AIFinding = {
      ...template,
      id: uid("aif"),
      status: "open",
      citations: { askIds: [r.id] },
      subjectAskId: r.id,
      createdAt: now(),
    };
    s.aiFindings = [finding, ...(s.aiFindings ?? [])];
    findingIds.push(finding.id);
  }
  r.aiReview = { state: verdict.state, findingIds, reviewedAt: now() };
  appendAudit(s, {
    actorId: HEIMDALL_ACTOR,
    actorOrganisationId: s.programme.oversightId,
    class: "system",
    description:
      verdict.state === "flagged"
        ? `Heimdall reviewed '${r.title}' and raised ${findingIds.length} finding${findingIds.length === 1 ? "" : "s"}.`
        : `Heimdall reviewed '${r.title}' · checks passed.`,
    scope: { programmeId: s.programme.id, projectId: r.projectId, subProjectId: r.subProjectId },
  });
  return s;
}

export function acceptFinding(seed: WorkspaceSeed, findingId: string, actorId: string): WorkspaceSeed {
  const s = clone(seed);
  const f = (s.aiFindings ?? []).find((x) => x.id === findingId);
  if (!f) return s;
  f.status = "accepted";
  f.decidedById = actorId;
  f.decidedAt = now();
  const ask = s.spendRequests.find((r) => r.id === f.subjectAskId);
  appendAudit(s, {
    actorId,
    actorOrganisationId: ask?.organisationId ?? s.programme.oversightId,
    class: "status_change",
    description: `Accepted Heimdall finding: ${truncate(f.statement)}`,
    scope: { programmeId: s.programme.id, projectId: ask?.projectId, subProjectId: ask?.subProjectId },
  });
  return s;
}

export function dismissFinding(seed: WorkspaceSeed, findingId: string, actorId: string, reason: string): WorkspaceSeed {
  const s = clone(seed);
  const f = (s.aiFindings ?? []).find((x) => x.id === findingId);
  if (!f) return s;
  f.status = "dismissed";
  f.dismissReason = reason;
  f.decidedById = actorId;
  f.decidedAt = now();
  const ask = s.spendRequests.find((r) => r.id === f.subjectAskId);
  appendAudit(s, {
    actorId,
    actorOrganisationId: ask?.organisationId ?? s.programme.oversightId,
    class: "status_change",
    description: `Dismissed Heimdall finding: ${reason}`,
    scope: { programmeId: s.programme.id, projectId: ask?.projectId, subProjectId: ask?.subProjectId },
  });
  return s;
}

// ---------------------------------------------------------------------------
// Sovereignty Bill compliance — declarations register (pilot-readiness addendum §5)
// ---------------------------------------------------------------------------

export function fileDeclaration(seed: WorkspaceSeed, declarationId: string, actorId: string): WorkspaceSeed {
  const s = clone(seed);
  const decl = (s.declarations ?? []).find((x) => x.id === declarationId);
  if (!decl || decl.status === "filed") return s;
  decl.status = "filed";
  decl.filedAt = now();
  decl.filedById = actorId;
  appendAudit(s, {
    actorId,
    actorOrganisationId: s.programme.oversightId,
    class: "status_change",
    description: `Filed the inbound-funds declaration '${decl.title}' (${human(decl.amount)}) with the ${decl.authority}.`,
    scope: { programmeId: s.programme.id, projectId: s.milestones.find((m) => m.id === decl.milestoneId)?.projectId },
  });
  return s;
}

// ---------------------------------------------------------------------------
// Mobile-money statement import (pilot-readiness addendum §4.2)
// A branded variant of the CSV-reconcile flow: a mocked MoMo statement to reconcile.
// ---------------------------------------------------------------------------

const MOMO_STATEMENT: Omit<FeedTransaction, "id" | "connectionId" | "organisationId" | "status">[] = [
  { date: "2026-06-09", description: "MoMo payment · Lakeland Builders Merchants", amount: 3_200_000, direction: "out", counterparty: "Lakeland Builders Merchants", suggestedCategory: "Materials", suggestedProjectId: "fs-04-entebbe", suggestedSubProjectId: "sp-civil" },
  { date: "2026-06-09", description: "MoMo payment · casual site labour", amount: 1_650_000, direction: "out", counterparty: "Direct labour", suggestedCategory: "Labour", suggestedProjectId: "fs-04-entebbe", suggestedSubProjectId: "sp-civil" },
  { date: "2026-06-08", description: "MoMo payment · Total Energies fuel", amount: 540_000, direction: "out", counterparty: "Total Energies", suggestedCategory: "Logistics and transport", suggestedProjectId: "fs-04-entebbe", suggestedSubProjectId: "sp-civil" },
];

export function importMobileMoneyStatement(seed: WorkspaceSeed, orgId: string, actorId: string): WorkspaceSeed {
  const s = clone(seed);
  const conn = (s.connections ?? []).find((c) => c.organisationId === orgId && c.kind === "mobile_money");
  const rows: FeedTransaction[] = MOMO_STATEMENT.map((r) => ({
    ...r,
    id: uid("ft"),
    connectionId: conn?.id ?? "momo",
    organisationId: orgId,
    status: "unreconciled",
  }));
  s.feedTransactions = [...rows, ...(s.feedTransactions ?? [])];
  appendAudit(s, {
    actorId,
    actorOrganisationId: orgId,
    class: "system",
    description: `Imported a mobile-money statement · ${rows.length} transactions ready to reconcile.`,
    scope: { programmeId: s.programme.id },
  });
  return s;
}

// ---------------------------------------------------------------------------
// Planning + implementation (pilot-readiness addendum §3)
// The plan is built before spend starts; revising it after approval is audited.
// ---------------------------------------------------------------------------

export function setMilestonePlan(
  seed: WorkspaceSeed,
  milestoneId: string,
  patch: { targetDate?: string; plannedBudget?: number; evidenceRequirements?: string[] },
  actorId: string,
): WorkspaceSeed {
  const s = clone(seed);
  const m = s.milestones.find((x) => x.id === milestoneId);
  if (!m) return s;
  const changes: string[] = [];
  if (patch.targetDate && patch.targetDate !== m.targetDate) {
    changes.push(`target date ${m.targetDate} → ${patch.targetDate}`);
    m.targetDate = patch.targetDate;
  }
  if (patch.plannedBudget != null && patch.plannedBudget !== m.plannedBudget) {
    changes.push(`budget ${human(m.plannedBudget ?? 0)} → ${human(patch.plannedBudget)}`);
    m.plannedBudget = patch.plannedBudget;
  }
  if (patch.evidenceRequirements) {
    m.evidenceRequirements = patch.evidenceRequirements;
    changes.push("evidence requirements updated");
  }
  if (!changes.length) return s;
  m.planRevisions = (m.planRevisions ?? 0) + 1;
  appendAudit(s, {
    actorId,
    actorOrganisationId: s.programme.recipientId,
    class: "status_change",
    description: `Revised the plan for '${m.name}' · ${changes.join("; ")}.`,
    scope: { programmeId: s.programme.id, projectId: m.projectId },
  });
  return s;
}

/** Adds a new milestone to a project — the demo-mode counterpart to the
 * real API's POST /project-management/project/:id/milestones, used by
 * useCreateMilestone (CreateProjectModal/EditProjectModal's Milestones
 * section). */
export function addMilestone(
  seed: WorkspaceSeed,
  projectId: string,
  input: { milestonenumber: string; description: string; targetDate?: string; plannedBudget?: number },
  actorId: string,
): WorkspaceSeed {
  const s = clone(seed);
  const m: Milestone = {
    id: uid("ms"),
    projectId,
    name: input.description || input.milestonenumber,
    targetDate: input.targetDate ?? "",
    status: "planned",
    budgetSharePct: 0,
    description: input.description,
    plannedBudget: input.plannedBudget,
  };
  s.milestones = [...s.milestones, m];
  appendAudit(s, {
    actorId,
    actorOrganisationId: s.programme.recipientId,
    class: "system",
    description: `Added milestone '${m.name}' to ${projectName(s, projectId)}.`,
    scope: { programmeId: s.programme.id, projectId },
  });
  return s;
}

/** Mocked AI deal-onboarding: pre-fill evidence requirements where missing, marked as a draft to confirm. */
export function importProcurementPlanDraft(seed: WorkspaceSeed, projectId: string, actorId: string): WorkspaceSeed {
  const s = clone(seed);
  const defaults = ["Photographs per work package", "Contractor sign-off", "Delivery / completion notes"];
  let filled = 0;
  for (const m of s.milestones.filter((x) => x.projectId === projectId)) {
    if (!m.evidenceRequirements?.length) {
      m.evidenceRequirements = defaults;
      filled += 1;
    }
  }
  appendAudit(s, {
    actorId,
    actorOrganisationId: s.programme.recipientId,
    class: "system",
    description: `Imported a procurement plan draft for ${projectName(s, projectId)} · pre-filled ${filled} milestone${filled === 1 ? "" : "s"} for human confirmation.`,
    scope: { programmeId: s.programme.id, projectId },
  });
  return s;
}

// ---------------------------------------------------------------------------
// Site visits (Heimdall brief §5) — structured oversight capture, analysed live
// against the ledger, then a drafted visit report under human review.
// ---------------------------------------------------------------------------

/**
 * The ordered question labels a new visit's checklist is built from: the compulsory
 * core (always present, from code) followed by oversight's custom questions. Single
 * source of truth so the local mutation and the backend POST (useMutations) match.
 * `customOverride` lets the non-demo path pass questions fetched from the backend
 * instead of the (static) seed blob.
 */
export function siteVisitChecklistLabels(
  seed: WorkspaceSeed,
  customOverride?: { label: string; compulsory?: boolean }[],
): string[] {
  const custom = (customOverride ?? seed.siteVisitQuestions ?? []).filter((q) => !q.compulsory);
  return [...COMPULSORY_SITE_VISIT_QUESTIONS, ...custom].map((q) => q.label);
}

/** Start a fresh site visit, snapshotting the current oversight question template into its checklist. */
export function startSiteVisit(
  seed: WorkspaceSeed,
  input: { projectId: string; milestoneId?: string; visitorId: string },
  overrideId?: string,
): { seed: WorkspaceSeed; id: string } {
  const s = clone(seed);
  const id = overrideId ?? uid("sv");
  const ms = input.milestoneId
    ? s.milestones.find((m) => m.id === input.milestoneId)
    : s.milestones.find((m) => m.projectId === input.projectId && m.current);
  const reqs = siteVisitChecklistLabels(s);
  const visit: SiteVisit = {
    id,
    projectId: input.projectId,
    milestoneId: ms?.id,
    visitorId: input.visitorId,
    status: "in_progress",
    startedAt: now(),
    checklist: reqs.map((label, i) => ({ id: `${id}-c${i}`, label, judgement: "unverified", captureIds: [] })),
    captures: [],
    findingIds: [],
  };
  s.siteVisits = [visit, ...(s.siteVisits ?? [])];
  appendAudit(s, {
    actorId: input.visitorId,
    actorOrganisationId: s.programme.oversightId,
    class: "evidence",
    description: `Started a site visit at ${projectName(s, input.projectId)}${ms ? ` · ${ms.name}` : ""}.`,
    scope: { programmeId: s.programme.id, projectId: input.projectId },
  });
  return { seed: s, id };
}

// --- Oversight-managed question template (programme-wide) -------------------

/** Count of oversight-added (non-compulsory) custom questions in the template. */
export function customQuestionCount(seed: WorkspaceSeed): number {
  return (seed.siteVisitQuestions ?? []).filter((q) => !q.compulsory).length;
}

/** Add a custom question to the template. Enforces the MAX_CUSTOM_QUESTIONS cap and ignores blanks. */
export function addSiteVisitQuestion(seed: WorkspaceSeed, label: string, actorId: string): WorkspaceSeed {
  const s = clone(seed);
  const trimmed = label.trim();
  if (!trimmed) return s;
  s.siteVisitQuestions = s.siteVisitQuestions ?? [];
  if (customQuestionCount(s) >= MAX_CUSTOM_QUESTIONS) return s;
  s.siteVisitQuestions.push({ id: uid("svq"), label: trimmed, compulsory: false });
  appendAudit(s, {
    actorId,
    actorOrganisationId: s.programme.oversightId,
    class: "system",
    description: `Added a site-visit question: “${trimmed}”.`,
    scope: { programmeId: s.programme.id },
  });
  return s;
}

/** Edit a custom question's label. Compulsory questions are locked (no-op). */
export function updateSiteVisitQuestion(
  seed: WorkspaceSeed,
  id: string,
  label: string,
  actorId: string,
): WorkspaceSeed {
  const s = clone(seed);
  const trimmed = label.trim();
  const q = (s.siteVisitQuestions ?? []).find((x) => x.id === id);
  if (!q || q.compulsory || !trimmed || q.label === trimmed) return s;
  const before = q.label;
  q.label = trimmed;
  appendAudit(s, {
    actorId,
    actorOrganisationId: s.programme.oversightId,
    class: "system",
    description: `Edited a site-visit question: “${before}” → “${trimmed}”.`,
    scope: { programmeId: s.programme.id },
  });
  return s;
}

/** Remove a custom question. Compulsory questions are locked (no-op). */
export function removeSiteVisitQuestion(seed: WorkspaceSeed, id: string, actorId: string): WorkspaceSeed {
  const s = clone(seed);
  const q = (s.siteVisitQuestions ?? []).find((x) => x.id === id);
  if (!q || q.compulsory) return s;
  s.siteVisitQuestions = (s.siteVisitQuestions ?? []).filter((x) => x.id !== id);
  appendAudit(s, {
    actorId,
    actorOrganisationId: s.programme.oversightId,
    class: "system",
    description: `Removed a site-visit question: “${q.label}”.`,
    scope: { programmeId: s.programme.id },
  });
  return s;
}

/**
 * Pull newly-added template questions into an in-progress visit, appending them
 * as unanswered items. Never touches recorded answers, and refuses on anything
 * that is not in progress (a completed visit's checklist is frozen for audit).
 */
export function syncVisitQuestions(seed: WorkspaceSeed, visitId: string): WorkspaceSeed {
  const s = clone(seed);
  const v = (s.siteVisits ?? []).find((x) => x.id === visitId);
  if (!v || v.status !== "in_progress") return s;
  const existing = new Set(v.checklist.map((c) => c.label));
  // Draw from the same effective template the banner counts against (compulsory
  // core + oversight's custom questions), so what's offered is what gets added.
  const additions = siteVisitChecklistLabels(s).filter((label) => !existing.has(label));
  if (!additions.length) return s;
  const base = v.checklist.length;
  v.checklist = [
    ...v.checklist,
    ...additions.map((label, i) => ({
      id: `${v.id}-c${base + i}`,
      label,
      judgement: "unverified" as const,
      captureIds: [] as string[],
    })),
  ];
  return s;
}

/** Move a planned visit into the field. */
export function beginVisit(seed: WorkspaceSeed, visitId: string): WorkspaceSeed {
  const s = clone(seed);
  const v = (s.siteVisits ?? []).find((x) => x.id === visitId);
  if (v && v.status === "planned") {
    v.status = "in_progress";
    v.startedAt = now();
  }
  return s;
}

export function setChecklistJudgement(seed: WorkspaceSeed, visitId: string, itemId: string, judgement: VisitJudgement): WorkspaceSeed {
  const s = clone(seed);
  const v = (s.siteVisits ?? []).find((x) => x.id === visitId);
  const item = v?.checklist.find((c) => c.id === itemId);
  if (item) item.judgement = judgement;
  return s;
}

/** Fallback image for a photo capture recorded without a real file (keeps the demo looking real). */
const PLACEHOLDER_CAPTURE_PHOTO =
  "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=1200&q=60";

export function captureVisitItem(
  seed: WorkspaceSeed,
  visitId: string,
  input: { type: "photo" | "note"; note?: string; url?: string; checklistItemId?: string },
): WorkspaceSeed {
  const s = clone(seed);
  const v = (s.siteVisits ?? []).find((x) => x.id === visitId);
  if (!v) return s;
  const project = s.projects.find((p) => p.id === v.projectId);
  const capId = uid("cap");
  v.captures = [
    ...v.captures,
    {
      id: capId,
      type: input.type,
      geo: project?.lat != null && project?.lng != null ? { lat: project.lat, lng: project.lng } : undefined,
      takenAt: now(),
      note: input.note,
      // Use the real uploaded image where one was provided, else a placeholder.
      url: input.type === "photo" ? (input.url ?? PLACEHOLDER_CAPTURE_PHOTO) : undefined,
      linkedChecklistItemId: input.checklistItemId,
    },
  ];
  if (input.checklistItemId) {
    const item = v.checklist.find((c) => c.id === input.checklistItemId);
    if (item) item.captureIds = [...item.captureIds, capId];
  }
  return s;
}

export function completeVisit(seed: WorkspaceSeed, visitId: string, summary?: string): WorkspaceSeed {
  const s = clone(seed);
  const v = (s.siteVisits ?? []).find((x) => x.id === visitId);
  if (!v) return s;
  v.status = "complete";
  v.completedAt = now();
  if (summary) v.summary = summary;
  appendAudit(s, {
    actorId: v.visitorId,
    actorOrganisationId: s.programme.oversightId,
    class: "evidence",
    description: `Completed the site visit at ${projectName(s, v.projectId)}.`,
    scope: { programmeId: s.programme.id, projectId: v.projectId },
  });
  return s;
}

/** Compose a drafted visit report from the captures and checklist (Heimdall draft, human-reviewed). */
export function generateVisitReportDraft(
  seed: WorkspaceSeed,
  visitId: string,
  authorId: string,
  overrideReportId?: string,
): { seed: WorkspaceSeed; reportId: string } {
  const s = clone(seed);
  const v = (s.siteVisits ?? []).find((x) => x.id === visitId);
  if (!v) return { seed: s, reportId: "" };
  const project = s.projects.find((p) => p.id === v.projectId);
  const ms = s.milestones.find((m) => m.id === v.milestoneId);
  const reportId = overrideReportId ?? uid("rep");
  const discrepancies = v.checklist.filter((c) => c.judgement === "not_as_claimed" || c.judgement === "partial");
  const verifiedCount = v.checklist.filter((c) => c.judgement === "as_claimed").length;
  const report: Report = {
    id: reportId,
    title: `Site visit · ${project?.name ?? "project"}${ms ? `, ${ms.name}` : ""}`,
    type: "Update",
    scope: "project",
    programmeId: s.programme.id,
    projectId: v.projectId,
    audience: "Oversight and the Danish Impact Fund",
    periodLabel: "Site visit",
    periodStart: v.startedAt ?? now(),
    periodEnd: v.completedAt ?? now(),
    createdAt: now(),
    updatedAt: now(),
    authorId,
    visibleTo: ["oversight", "funder"],
    kind: "visit",
    draftedByAI: true,
    siteVisitId: v.id,
    sections: [
      { heading: "Overview", body: `Site visit to ${project?.name ?? "the project"}${ms ? ` during the ${ms.name} milestone` : ""}. ${v.checklist.length} checklist items were walked; ${verifiedCount} verified as claimed, ${discrepancies.length} with a discrepancy.` },
      { heading: "Verification by checklist item", body: v.checklist.map((c) => `${c.label}: ${JUDGEMENT_LABEL[c.judgement]}.`).join(" ") },
      ...(discrepancies.length ? [{ heading: "Discrepancies", body: discrepancies.map((c) => `${c.label} · observed ${JUDGEMENT_LABEL[c.judgement].toLowerCase()}.`).join(" ") }] : []),
      { heading: "Recommended actions", body: discrepancies.length ? "Query the affected spend asks with the captured evidence attached, and schedule a follow-up on the open items." : "No actions required; the milestone evidence is consistent with what was observed on site." },
    ],
  };
  s.reports = [report, ...(s.reports ?? [])];
  v.reportId = reportId;
  return { seed: s, reportId };
}

/** Approve a drafted visit report: file it, record the reviewer, and attach discrepancies as findings. */
export function approveVisitReport(seed: WorkspaceSeed, visitId: string, reviewerId: string): WorkspaceSeed {
  const s = clone(seed);
  const v = (s.siteVisits ?? []).find((x) => x.id === visitId);
  if (!v) return s;
  const report = s.reports?.find((r) => r.id === v.reportId);
  if (report) report.reviewedById = reviewerId;

  // Attach each discrepancy as a Heimdall finding on a milestone ask (best-effort).
  const milestoneAsk = s.spendRequests.find((r) => r.milestoneId === v.milestoneId && r.state !== "rejected");
  const newFindingIds: string[] = [];
  for (const item of v.checklist.filter((c) => c.judgement === "not_as_claimed" || c.judgement === "partial")) {
    const finding: AIFinding = {
      id: uid("aif"),
      severity: item.judgement === "not_as_claimed" ? "serious" : "attention",
      category: "progress",
      statement: `Site visit found '${item.label}' ${JUDGEMENT_LABEL[item.judgement].toLowerCase()} versus the ledger claim. Recommend querying the related spend.`,
      citations: { captureIds: item.captureIds, askIds: milestoneAsk ? [milestoneAsk.id] : [] },
      confidence: "high",
      status: "open",
      subjectAskId: milestoneAsk?.id,
      subjectVisitId: v.id,
      createdAt: now(),
    };
    s.aiFindings = [finding, ...(s.aiFindings ?? [])];
    newFindingIds.push(finding.id);
  }
  v.findingIds = [...(v.findingIds ?? []), ...newFindingIds];

  appendAudit(s, {
    actorId: reviewerId,
    actorOrganisationId: s.programme.oversightId,
    class: "evidence",
    description: `Approved the site visit report for ${projectName(s, v.projectId)}${newFindingIds.length ? ` · ${newFindingIds.length} discrepancy finding${newFindingIds.length === 1 ? "" : "s"} attached.` : "."}`,
    scope: { programmeId: s.programme.id, projectId: v.projectId },
  });
  return s;
}

const JUDGEMENT_LABEL: Record<VisitJudgement, string> = {
  as_claimed: "As claimed",
  partial: "Partially as claimed",
  not_as_claimed: "Not as claimed",
  unverified: "Could not verify",
};

// ---------------------------------------------------------------------------
// Local helpers
// ---------------------------------------------------------------------------

function projectName(seed: WorkspaceSeed, projectId: string): string {
  return seed.projects.find((p) => p.id === projectId)?.name ?? projectId;
}
function human(raw: number): string {
  if (raw >= 1_000_000_000) {
    const v = raw / 1_000_000_000;
    return `${v < 10 ? v.toFixed(2) : Math.round(v)}B UGX`;
  }
  if (raw >= 1_000_000) {
    const v = raw / 1_000_000;
    return `${Number.isInteger(v) ? v : v.toFixed(1)}M UGX`;
  }
  return `${raw.toLocaleString()} UGX`;
}
function truncate(t: string, n = 60): string {
  return t.length > n ? t.slice(0, n) + "…" : t;
}
